import React, { useMemo } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import { provinceList, Timezones } from '../../../../helper';
import settings from '../../../../config/settings';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import SectionCard, { SubGroup } from '../ui/SectionCard';
import Field from '../ui/Field';
import Toggle from '../ui/Toggle';
import { TextInput, NumberInput, SelectInput } from '../ui/Inputs';
import { brand, gray, ink, white, radius, shadow } from '../theme';

/**
 * Section 5 — Location. Ports `AddProgramModal.js:2124-2277`.
 *
 * Everything from Location down to Zip Code sits behind the Online Program checkbox —
 * ticking it hides the whole address block, timezone included.
 *
 * The District select is gated on a single hardcoded camp id, so it renders for exactly
 * one customer. Its save line is commented out in the payload; both are preserved as-is.
 */
const Location = () => {
    const {
        userInput,
        setUserInput,
        setError,
        district,
        setDistrict,
        districtList,
        campId,
        handleChange,
        handleSelect,
        handleNumberChange,
        handleCheckboxChange,
        handleProgramTimeZoneChange,
        fieldStatus,
        requiredOnly,
    } = useProgramForm();

    const handleLocationChange = value => {
        setUserInput({ location: value });
        setError({ location: '', lat: '', addressLine1: '', city: '', state: '' });
    };

    // Selecting a suggestion back-fills the address parts and the lat/long the map needs.
    // Ported from `AddProgramModal.js:469-525`.
    const handleLocationSelect = value => {
        setUserInput({ location: value });

        geocodeByAddress(value)
            .then(results => {
                for (let i = 0; i < results.length; i++) {
                    for (let j = 0; j < results[i].address_components.length; j++) {
                        const component = results[i].address_components[j];
                        for (let k = 0; k < component.types.length; k++) {
                            if (component.types[k] === 'route') {
                                setUserInput({ addressLine1: component.long_name || '' });
                            }
                            if (component.types[k] === 'postal_code') {
                                setUserInput({ zipCode: component.short_name || '' });
                            }
                            if (component.types[k] === 'locality') {
                                setUserInput({ city: component.long_name || '' });
                            }
                            if (component.types[k] === 'administrative_area_level_1') {
                                setUserInput({ state: component.short_name || '' });
                            }
                        }
                    }
                }
                return results;
            })
            .then(results => getLatLng(results[0]))
            .then(latLng => setUserInput({ lat: latLng.lat, long: latLng.lng }))
            .catch(err => console.error('Error', err));
    };

    // A saved state that is no longer in the list still has to display.
    const stateNotInList =
        userInput.state && !provinceList.find(item => item.value === userInput.state);

    // `provinceList` is `{ value: 'CA', label: 'California' }`; the old native <select>
    // built its "CA - California" text in the markup, so the label is composed here to keep
    // the rendered options identical. A state that has since left the list is appended so
    // editing an old program does not silently blank it — what `stateNotInList` did before.
    const stateOptions = useMemo(() => {
        const options = provinceList.map(item => ({
            value: item.value,
            label: `${item.value} - ${item.label}`,
        }));
        return stateNotInList
            ? [...options, { value: userInput.state, label: userInput.state }]
            : options;
    }, [stateNotInList, userInput.state]);

    return (
        <SectionCard
            id="location"
            step={5}
            title="Location"
            description="Where the program runs and which timezone applies"
        >
            <div className="mb-3">
                <Toggle
                    id="isOnline"
                    label="Online Program?"
                    checked={Boolean(userInput.isOnline)}
                    onChange={checked => handleCheckboxChange({ target: { checked } })}
                    labelStyle={{ color: ink[700] }}
                />
            </div>

            {!userInput.isOnline ? (
                <SubGroup title="Address" description="Used for maps, directions and tax.">
                    <div className="row">
                        <Field
                            className="col-12 mb-3"
                            label="Location"
                            htmlFor="location"
                            required
                            hint="Start typing and pick a suggestion — the address fields fill in automatically."
                            {...fieldStatus('location')}
                        >
                            <PlacesAutocomplete
                                value={userInput.location}
                                onChange={handleLocationChange}
                                onSelect={handleLocationSelect}
                            >
                                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            id="location"
                                            className={`pf-control ${fieldStatus('location').error ? 'is-invalid' : ''}`}
                                            {...getInputProps({ placeholder: 'Enter location' })}
                                        />
                                        {loading || suggestions.length ? (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    zIndex: 20,
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    marginTop: '4px',
                                                    backgroundColor: white,
                                                    border: `1px solid ${gray[200]}`,
                                                    borderRadius: radius.md,
                                                    boxShadow: shadow.card,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {loading ? (
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: ink[400] }}>
                                                        Loading…
                                                    </div>
                                                ) : ''}
                                                {suggestions.map(suggestion => {
                                                    const { key, ...itemProps } = getSuggestionItemProps(suggestion);
                                                    return (
                                                        <div
                                                            key={suggestion.placeId}
                                                            {...itemProps}
                                                            style={{
                                                                padding: '8px 12px',
                                                                fontSize: '13px',
                                                                cursor: 'pointer',
                                                                backgroundColor: suggestion.active ? brand[50] : white,
                                                                color: suggestion.active ? brand[700] : ink[700],
                                                            }}
                                                        >
                                                            {suggestion.description}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : ''}
                                    </div>
                                )}
                            </PlacesAutocomplete>
                        </Field>

                        <Field
                            className="col-md-6 mb-3"
                            label="Program Timezone"
                            htmlFor="programTimezone"
                            required
                            {...fieldStatus('programTimezone')}
                        >
                            <SelectInput
                                id="programTimezone"
                                options={Timezones}
                                value={Timezones.find(tz => tz.value === userInput.programTimezone)}
                                change={handleProgramTimeZoneChange}
                                pHolder="Select a program timezone"
                                error={fieldStatus('programTimezone').error}
                            />
                        </Field>

                        <Field
                            className="col-md-6 mb-3"
                            label="Street Address"
                            htmlFor="addressLine1"
                            required
                            {...fieldStatus('addressLine1')}
                        >
                            <TextInput
                                id="addressLine1"
                                name="addressLine1"
                                value={userInput.addressLine1}
                                change={handleChange}
                                pHolder="Street Address"
                                error={fieldStatus('addressLine1').error}
                            />
                        </Field>

                        <Field
                            className="col-md-4 mb-3"
                            label="City"
                            htmlFor="city"
                            required
                            {...fieldStatus('city')}
                        >
                            <TextInput
                                id="city"
                                name="city"
                                value={userInput.city}
                                change={handleChange}
                                pHolder="City"
                                error={fieldStatus('city').error}
                            />
                        </Field>

                        <Field
                            className="col-md-4 mb-3"
                            label="State"
                            htmlFor="state"
                            required
                            {...fieldStatus('state')}
                        >
                            <SelectInput
                                id="state"
                                options={stateOptions}
                                value={stateOptions.find(opt => opt.value === userInput.state) || null}
                                change={e => handleSelect(e, 'state')}
                                pHolder="Select State"
                                error={fieldStatus('state').error}
                            />
                        </Field>

                        <Field
                            className="col-md-4 mb-3"
                            label="Zip Code"
                            htmlFor="zipCode"
                            {...fieldStatus('zipCode')}
                        >
                            <TextInput
                                id="zipCode"
                                name="zipCode"
                                value={userInput.zipCode}
                                change={handleChange}
                                pHolder="Zip Code"
                                error={fieldStatus('zipCode').error}
                            />
                        </Field>
                    </div>
                </SubGroup>
            ) : ''}

            <div className="row mt-3">
                {!requiredOnly ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Tax"
                        htmlFor="tax"
                        hint="State tax applied at checkout."
                        {...fieldStatus('tax')}
                    >
                        <NumberInput
                            id="tax"
                            change={e => handleNumberChange(e, 'tax')}
                            value={userInput.tax}
                            min={0}
                            max={99}
                            precision={userInput.tax ? 2 : 0}
                            pHolder="State tax"
                            formatter={value => `${value}%`}
                        />
                    </Field>
                ) : ''}

                {/* Single-camp feature: renders only for the district camp. The matching
                    payload line is commented out upstream, so the choice is not saved. */}
                {campId === settings.api.districtCamp ? (
                    <Field className="col-md-6 mb-3" label="District" htmlFor="district">
                        <SelectInput
                            id="district"
                            pHolder="Select District"
                            change={e => setDistrict(e)}
                            // `district` starts life as `{}` and becomes a valueless option once
                            // cleared. react-select treats either as a selected option whose
                            // label is undefined and renders a blank control, so the placeholder
                            // is only reachable by handing it an outright null.
                            value={district?.value ? district : null}
                            options={districtList}
                        />
                    </Field>
                ) : ''}
            </div>
        </SectionCard>
    );
};

export default Location;
